/**
 * OTP Convert Dialog Web Component
 * Displays a modal encouraging users to convert from one-time purchase to subscription
 */
class OTPConvertDialog extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      activeClass: 'active',
      bodyOpenClass: 'otp-convert-dialog-open'
    };

    this.overlay = this.querySelector('.otp-convert-dialog__overlay');
    this.container = this.querySelector('.otp-convert-dialog__container');
    this.closeBtn = this.querySelector('.otp-convert-dialog__close');
    this.primaryBtn = this.querySelector('.otp-convert-dialog__btn-primary');
    this.secondaryBtn = this.querySelector('.otp-convert-dialog__btn-secondary');

    // Callback functions
    this.onSubscribeCallback = null;
    this.onDeclineCallback = null;

    // Store the element that had focus before opening
    this.previousActiveElement = null;

    this.bindEvents();
  }

  connectedCallback() {
    // Support Shopify theme editor
    if (Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:select', this.onSectionSelect.bind(this));
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect.bind(this));
    }
  }

  disconnectedCallback() {
    if (Shopify && Shopify.designMode) {
      document.removeEventListener('shopify:section:select', this.onSectionSelect.bind(this));
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect.bind(this));
    }
  }

  onSectionSelect(event) {
    if (event.detail.sectionId === this.dataset.sectionId) {
      this.show();
    }
  }

  onSectionDeselect(event) {
    if (event.detail.sectionId === this.dataset.sectionId) {
      this.hide();
    }
  }

  bindEvents() {
    // Close button click - continue with OTP
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.decline());
    }

    // Overlay click (close when clicking outside container) - continue with OTP
    if (this.overlay) {
      this.overlay.addEventListener('click', (event) => {
        if (event.target === this.overlay) {
          this.decline();
        }
      });
    }

    // Primary CTA click (Subscribe)
    if (this.primaryBtn) {
      this.primaryBtn.addEventListener('click', () => {
        // Store callback before hide() clears it
        const callback = this.onSubscribeCallback;
        this.hide();
        if (typeof callback === 'function') {
          callback();
        }
      });
    }

    // Secondary CTA click (Not Now) - continue with OTP
    if (this.secondaryBtn) {
      this.secondaryBtn.addEventListener('click', () => this.decline());
    }

    // Escape key to close - continue with OTP
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains(this.classes.activeClass)) {
        this.decline();
      }
    });
  }

  /**
   * Decline subscription and continue with OTP
   * Called when user closes dialog in any way except clicking Subscribe
   */
  decline() {
    // Store callback before hide() clears it
    const callback = this.onDeclineCallback;
    this.hide();
    if (typeof callback === 'function') {
      callback();
    }
  }

  /**
   * Show the dialog
   * @param {Object} options - Configuration options
   * @param {Function} options.onSubscribe - Callback when user clicks Subscribe
   * @param {Function} options.onDecline - Callback when user clicks Not Now
   */
  show(options = {}) {
    console.log('[OTP-Convert-Dialog] Showing dialog');

    // Store callbacks
    this.onSubscribeCallback = options.onSubscribe || null;
    this.onDeclineCallback = options.onDecline || null;

    // Store current focus
    this.previousActiveElement = document.activeElement;

    // Show dialog
    this.classList.add(this.classes.activeClass);

    // Prevent body scroll
    document.body.classList.add(this.classes.bodyOpenClass);

    // Set up focus trap
    this.trapFocus();

    // Announce to screen readers
    this.setAttribute('aria-hidden', 'false');

    // Dispatch event
    this.dispatchEvent(new CustomEvent('otp-dialog:opened'));
  }

  /**
   * Hide the dialog
   */
  hide() {
    console.log('[OTP-Convert-Dialog] Hiding dialog');

    // Hide dialog
    this.classList.remove(this.classes.activeClass);

    // Restore body scroll
    document.body.classList.remove(this.classes.bodyOpenClass);

    // Restore focus
    if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
      this.previousActiveElement.focus();
    }

    // Update accessibility
    this.setAttribute('aria-hidden', 'true');

    // Clear callbacks
    this.onSubscribeCallback = null;
    this.onDeclineCallback = null;

    // Dispatch event
    this.dispatchEvent(new CustomEvent('otp-dialog:closed'));
  }

  /**
   * Trap focus within the dialog for accessibility
   */
  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Focus the first focusable element
    firstFocusable.focus();

    // Handle tab key to keep focus within dialog
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    });
  }
}

// Define the custom element
if (!customElements.get('otp-convert-dialog')) {
  customElements.define('otp-convert-dialog', OTPConvertDialog);
}
